import { TextField, Box, Typography, Autocomplete, CircularProgress } from "@mui/material";
import React from "react";
import { GlobalButton, GlobalSecondaryButton } from "../../styles";
import ActionModal from "../ActionModal";
import { ProgressSpinner } from "../../../components/ProgressSpinner";
import {
  DistillationActionResult,
  DistillationExperiment,
  useDistillationExperimentActions,
} from "../hooks/useDistillationExperimentActions";

interface DistillationHeaderProps {
  experiment: DistillationExperiment;
  experimentIsStarting: boolean;
  experimentHasStarted: boolean;
  StartExperiment: () => void;
  onActionComplete: (result: DistillationActionResult) => void;
  handleExperimentChange: (field: string, value: any) => void;
  experimentErrors: any;
  updateExperimentErrors: (field: string, hasError: boolean) => void;
  ValidateInitialAmount: () => boolean;
  actualAmountMessage: string;
  allLocations: any[];
  disabled?: boolean;
  showLocationField?: boolean;
  requestedAmountLabel?: React.ReactNode;
  actualAmountField?: React.ReactNode;
}

export const DistillationHeader: React.FC<DistillationHeaderProps> = ({
  experiment,
  experimentIsStarting,
  experimentHasStarted,
  StartExperiment,
  onActionComplete,
  handleExperimentChange,
  experimentErrors,
  updateExperimentErrors,
  ValidateInitialAmount,
  actualAmountMessage,
  allLocations,
  disabled = false,
  showLocationField = true,
  requestedAmountLabel = null,
  actualAmountField = null,
}) => {
  const {
    isProcessingAction,
    activeModal,
    closeModal,
    canReturnToRequester,
    canCancelExperiment,
    startReturnToRequester,
    startCancelExperiment,
  } = useDistillationExperimentActions({
    onActionComplete,
  });

  const canSendExperimentBackToRequester = canReturnToRequester(experiment);
  const canCancelThisExperiment = canCancelExperiment(experiment);

  const handleStartExperiment = () => StartExperiment();
  const handleSendBackToRequester = () => startReturnToRequester(experiment);
  const handleCancelExperiment = () => startCancelExperiment(experiment);

  return (
    <>
      <Box display="flex" gap={3} alignItems="center" marginTop={"2rem"} width="50%">
        {experimentIsStarting && <CircularProgress style={{ textTransform: "none" }} />}
        <GlobalButton
          disabled={disabled || experimentHasStarted || experimentIsStarting}
          variant="contained"
          style={{ textTransform: "none", marginBottom: "25px" }}
          onClick={handleStartExperiment}>
          {"Start Experiment"}
        </GlobalButton>
        <GlobalSecondaryButton
          disabled={disabled || isProcessingAction || !canSendExperimentBackToRequester}
          variant="contained"
          style={{ textTransform: "none", marginBottom: "25px" }}
          onClick={handleSendBackToRequester}>
          {"Send Back to Requester"}
        </GlobalSecondaryButton>
        <GlobalSecondaryButton
          disabled={disabled || isProcessingAction || !canCancelThisExperiment}
          variant="contained"
          style={{ textTransform: "none", marginBottom: "25px" }}
          onClick={handleCancelExperiment}>
          {"Cancel Experiment"}
        </GlobalSecondaryButton>
      </Box>

      <Box display="flex" gap={3} alignItems="center" width="50%">
        {requestedAmountLabel ? (
          requestedAmountLabel
        ) : (
          <Typography fontSize={16}>
            Requested Initial Amount: {experiment.initialSampleAmount?.toLocaleString()}{" "}
            {experiment.initialSampleAmountUoM}
          </Typography>
        )}

        {actualAmountField ? (
          actualAmountField
        ) : (
          <TextField
            size="small"
            margin="normal"
            variant="outlined"
            label="Actual Initial Amount (g)"
            InputLabelProps={{ shrink: true }}
            disabled={disabled || experimentHasStarted}
            onChange={(e) => handleExperimentChange("actualInitialSampleAmount", e.target.value)}
            value={
              experiment.actualInitialSampleAmount === null
                ? ""
                : experiment.actualInitialSampleAmount.toLocaleString()
            }
            error={experimentErrors.actualInitialSampleAmount}
            onBlur={(e) => {
              updateExperimentErrors("actualInitialSampleAmount", ValidateInitialAmount());
            }}
            helperText={
              experimentErrors.actualInitialSampleAmount ? actualAmountMessage : ""
            }></TextField>
        )}

        {showLocationField && (
          <Autocomplete
            disabled={disabled || experimentHasStarted}
            disablePortal
            noOptionsText={"Loading Locations..."}
            options={allLocations ? allLocations : []}
            getOptionLabel={(option) => option.locationName}
            onChange={(e, value) => handleExperimentChange("experimentLocation", value)}
            isOptionEqualToValue={(option, value) => value.locationName === option.locationName}
            autoHighlight
            autoSelect
            value={experiment.experimentLocation}
            renderInput={(params) => (
              <TextField
                {...params}
                style={{ width: "200px" }}
                variant="outlined"
                size="small"
                margin="normal"
                label="Experiment Location"
                error={experimentErrors.experimentLocation}
                InputLabelProps={{ shrink: true }}
                inputProps={{ ...params.inputProps, style: { fontSize: 16 } }}
                InputProps={{ ...params.InputProps }}
              />
            )}
          />
        )}
      </Box>

      <ProgressSpinner display={isProcessingAction} />
      {activeModal && <ActionModal modal={activeModal} onClose={closeModal} open={!!activeModal} />}
    </>
  );
};
